import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import styles from './styles.module.scss'
import { TextBox } from 'srcReact/components/common2/TextBox'
import { checkNested, isEmpty } from 'srcReact/helpers/validator'
import { getColorHexCode } from 'srcReact/helpers/utils'
import { appConstantSettings } from 'srcReact/appConstantSettings'
import { organizationSettingsActions } from 'srcReact/OrganizationSettingsPage'
import DeleteOrganizationPopin from 'srcReact/components/popinsContent/DeleteOrganizationPopin'
import ColorPicker from 'srcReact/components/common2/ColorPicker'
import DesktopHeader from '../DesktopHeader'
import Button from 'srcReact/components/common2/Button'
import Spinner from 'srcReact/components/common2/Spinner'
import ErrorMessage from 'srcReact/components/common2/ErrorMessage'
import { getCurrentOrgId } from 'srcReact/helpers/organizationHelper'
import ImageCropper from 'srcReact/components/common2/ImageCropper'

const Display = () => {
  const { t } = useTranslation()

  const dispatch = useDispatch()
  const {
    common: {
      isMobile,
      fileCreation: { files: uploadedFiles, loadingFile, error }
    },
    organization: { current_organization },
    organizationSettings: { organizationInfo }
  } = useSelector((state) => state)

  const [showDeleteOrganizationModal, setShowDeleteOrganizationModal] = useState(false)
  const [showColorPicker, setShowColorPicker] = useState(false)
  const [nameAcrnChanged, setNameAcrnChanged] = useState(false)
  const [colorChanged, setColorChanged] = useState(false)
  const [isInitialized, setIsInitialized] = useState(false)

  const [orgColor, setOrgColor] = useState('')
  const [orgPicture, setOrgPicture] = useState('')
  const [orgName, setOrgName] = useState({
    value: organizationInfo?.info?.name || '',
    hasError: false,
    errorMessage: '',
    isRequired: false
  })
  const [logoError, setLogoError] = useState('')
  const [orgAcronym, setOrgAcronym] = useState({
    value: '',
    hasError: false,
    errorMessage: '',
    isRequired: false
  })

  useEffect(() => {
    if (checkNested(current_organization, 'info', 'id')) {
      if (current_organization.info.editable)
        dispatch(organizationSettingsActions.getOrganizationInfo(current_organization.info.id))
    }
  }, [current_organization && current_organization.info && current_organization.info.id])

  useEffect(() => {
    if (organizationInfo) {
      if (organizationInfo.success && !organizationInfo.loading) {
        setNameAcrnChanged(false)
        setColorChanged(false)
      }

      if (!isInitialized && organizationInfo.info?.id) {
        setOrgName({ ...orgName, value: organizationInfo.info?.name })
        setOrgAcronym({ ...orgAcronym, value: organizationInfo.organizations_basicsettings?.accronym })
        setOrgPicture(organizationInfo.info.picture)
        setOrgColor(organizationInfo.organizations_basicsettings.color)
        setIsInitialized(true)
      }
    }
  }, [organizationInfo])

  const validate = () => {
    if (isEmpty(orgName.value)) {
      setOrgName({
        ...orgName,
        hasError: true,
        errorMessage: t('error.required', { field: t('organizationSettings.display.labelOrganizationName') })
      })
      return false
    }
    if (isEmpty(orgAcronym.value)) {
      setOrgAcronym({
        ...orgAcronym,
        hasError: true,
        errorMessage: t('error.required', { field: t('organizationSettings.display.labelAcronym') })
      })
      return false
    }
    return true
  }

  const getInitialPayload = () => {
    let payload = null
    if (organizationInfo?.organizations_basicsettings && organizationInfo?.info) {
      const { accronym, color, google_translation_key, id_wp_sync_files_original } =
        organizationInfo.organizations_basicsettings
      const { id, name } = organizationInfo.info
      payload = {
        organization_id: id || getCurrentOrgId(),
        name: name,
        color: color,
        accronym: accronym,
        google_translation_key: google_translation_key,
        picture_id: id_wp_sync_files_original
      }
    }
    return payload
  }

  const getFileError = (fileError) => {
    setLogoError(fileError)
  }

  const handleSave = (field) => {
    if (field !== 'picture') {
      let payload = getInitialPayload()
      if (payload) {
        if (field === 'nameAcronym') {
          payload = { ...payload, name: orgName.value, accronym: orgAcronym.value }
        } else if (field === 'color') {
          payload = { ...payload, color: orgColor }
        }
        delete payload.picture_id
        if (validate()) {
          dispatch(organizationSettingsActions.editOrganizationInfo(payload))
        }
      }
    }
  }

  const handleCancel = (field) => {
    if (organizationInfo?.organizations_basicsettings && organizationInfo?.info) {
      const { accronym, color, google_translation_key } = organizationInfo.organizations_basicsettings
      const { id, name } = organizationInfo.info
      if (field === 'nameAcronym') {
        setOrgName({ ...orgName, value: name, hasError: false, errorMessage: '' })
        setOrgAcronym({ ...orgAcronym, value: accronym, hasError: false, errorMessage: '' })
        setNameAcrnChanged(false)
      } else if (field === 'color') {
        setColorChanged(false)
        setOrgColor(color)
      }
    }
  }

  const handleCancelPicture = () => {
    if (organizationInfo?.info) {
      setOrgPicture(organizationInfo.info?.picture)
    }
  }

  const handleNameChange = (e) => {
    setNameAcrnChanged(true)
    setOrgName({
      ...orgName,
      value: e.target.value,
      errorMessage: '',
      hasError: false
    })
  }
  const handleAcronymChange = (e) => {
    setNameAcrnChanged(true)
    setOrgAcronym({
      ...orgAcronym,
      value: e.target.value,
      errorMessage: '',
      hasError: false
    })
  }

  const handleColorChange = (e) => {
    setColorChanged(true)
    const selectedColor = appConstantSettings.COLORS.find((c) => c.hex == e.hex)
    setOrgColor(selectedColor?.name)
  }

  const renderUpdateButtons = (field, className) => (
    <div className={`${styles.buttons} ${className}`}>
      <Button
        style='secondary'
        customStyle={styles.cancelBtn}
        text={t('global.cancel')}
        handleClick={() => handleCancel(field)}
      />
      <Button
        customStyle={styles.updateBtn}
        text={organizationInfo.loading || loadingFile ? <Spinner /> : t('global.update')}
        handleClick={() => handleSave(field)}
      />
    </div>
  )

  const setOrgLogo = (picture) => {
    setOrgPicture(picture)

    let payload = getInitialPayload()
    if (payload) {
      dispatch(organizationSettingsActions.editOrganizationInfo({ ...payload, picture_id: picture.id }))
    }
  }

  return (
    <div className={styles.displayWrapper}>
      {!isMobile && (
        <DesktopHeader
          title={t('organizationSettings.display.title')}
          subtitle={t('organizationSettings.display.subtitle')}
        />
      )}

      <div className={styles.nameAccronym}>
        <h3 className={styles.name}>{t('organizationSettings.display.name')}</h3>
        <p className={styles.nameDescription}>{t('organizationSettings.display.nameDescription')}</p>
        <div className={styles.inputFields}>
          <div className={styles.inputName}>
            <TextBox
              name='name'
              fieldInfo={orgName}
              onChange={handleNameChange}
              customWrapperCss={styles.customWrapperCss}
              customInputCss={`${styles.inputClassName} ${orgName.hasError && styles.hasError}`}
              size='full'
              label={t('organizationSettings.display.labelOrganizationName')}
              labelPosition='bottom'
              customLabelCss={styles.customLabelCss}
              displayError={false}
              maxLength={appConstantSettings.ORG_NAME_MAXLENGTH}
            />
          </div>
          <div className={styles.inputAccronym}>
            <TextBox
              name='accronym'
              fieldInfo={orgAcronym}
              onChange={handleAcronymChange}
              customWrapperCss={styles.customWrapperCss}
              customInputCss={`${styles.inputClassName} ${orgAcronym.hasError && styles.hasError}`}
              size='full'
              label={t('organizationSettings.display.labelAcronym')}
              labelPosition='bottom'
              customLabelCss={styles.customLabelCss}
              displayError={false}
              maxLength={appConstantSettings.ACCRONYM_MAXLENGHT}
            />
          </div>

          {nameAcrnChanged && renderUpdateButtons('nameAcronym')}
        </div>
      </div>
      {orgName.errorMessage ? (
        <ErrorMessage text={orgName.errorMessage} />
      ) : orgAcronym.errorMessage ? (
        <ErrorMessage text={orgAcronym.errorMessage} />
      ) : null}

      <div className={styles.colorPicker}>
        <h3 className={styles.color}>{t('organizationSettings.display.color')}</h3>
        <p className={styles.colorDescription}>{t('organizationSettings.display.colorDescription')}</p>
        <div className={styles.colorInput}>
          <button className={styles.swatch} onClick={() => setShowColorPicker(!showColorPicker)}>
            <div
              className={styles.background}
              style={{
                backgroundColor: getColorHexCode(orgColor)
              }}
            />
          </button>
          {colorChanged && renderUpdateButtons('color', styles.colorUpdateButtons)}
        </div>
        {showColorPicker && (
          <ColorPicker
            colors={appConstantSettings.COLORS.map((c) => c.hex)}
            color={getColorHexCode(orgColor)}
            setColor={handleColorChange}
            setShowColorPicker={setShowColorPicker}
            showColorPicker={showColorPicker}
          />
        )}
      </div>

      <div className={styles.upload}>
        <h3 className={styles.logo}>{t('organizationSettings.display.logo')}</h3>
        <p className={styles.logoDescription}>{t('organizationSettings.display.logoDescription')}</p>
        <div className={styles.pictureWrapper}>
          <div className={styles.picture}>
            <ImageCropper
              editMode={true}
              isMobile={isMobile}
              recommendedFormatText={t('organization.create.helper_img')}
              hideSkipButton={true}
              aspectRatio={260 / 100}
              exisingImageUrl={orgPicture}
              showUploadZone={true}
              allowDelete={false}
              handleCancel={handleCancelPicture}
              handleNext={setOrgLogo}
              containerCSS={styles.imageCropperContainer}
              buttonWrapperCSS={styles.imageCropperActions}
            />
          </div>
        </div>
      </div>

      {logoError && logoError.length > 0 && <ErrorMessage text={logoError} />}

      <div className={styles.delete}>
        <button
          onClick={() => setShowDeleteOrganizationModal(!showDeleteOrganizationModal)}
          title={t('organizationSettings.display.deleteOrganization')}
          className={styles.deleteOrganization}
          type='button'>
          {t('organizationSettings.display.deleteOrganization')}
        </button>
        <p className={styles.deleteOrganizationDescription}>
          {t('organizationSettings.display.deleteOrganizationDescription')}
        </p>
      </div>

      <DeleteOrganizationPopin
        showDeleteOrganizationModal={showDeleteOrganizationModal}
        setShowDeleteOrganizationModal={() => setShowDeleteOrganizationModal(false)}
      />
    </div>
  )
}

export default Display
